import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchData, updateData, postDataNew } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Alert, Snackbar, Dialog, Button, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import StoreIcon from '@mui/icons-material/Store';
import EditIcon from '@mui/icons-material/Edit';
import ClaimsManager from './ClaimsManager';
import RolesManager from './RolesManager';
import BranchManager from './BranchManager';

const UserDetail = () => {
    useSessionValidation();

    const { id } = useParams();
    const { isAuthenticated, companyId, userId, token, logout } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); }
    }, [isAuthenticated, companyId]);

    const [isSavingRoles,    setIsSavingRoles]    = useState(false);
    const [isSavingClaims,   setIsSavingClaims]   = useState(false);
    const [isSavingBranches, setIsSavingBranches] = useState(false);
    const [isLoadingBranches,setIsLoadingBranches]= useState(false);
    const [isEditMode,       setIsEditMode]       = useState(false);
    const [isClaimEditMode,  setIsClaimEditMode]  = useState(false);
    const [isBranchEditMode, setIsBranchEditMode] = useState(false);
    const [successMessage,   setSuccessMessage]   = useState('');
    const [errorMessage,     setErrorMessage]     = useState('');

    const [formData, setFormData] = useState({
        FirstName: '', LastName: '', UserName: '',
        Email: '', ContactNumber: '', Roles: [],
    });
    const [formDataClaim,   setFormDataClaim]   = useState({ Claim: [] });
    const [formDataBranch,  setFormDataBranch]  = useState({ Branches: [] });

    useEffect(() => {
        fetchData('UserApi', `/UserEdit/${id}`, token)
            .then(response => {
                if (response.responseCode === '1111') {
                    const { User } = response.response_data;
                    setFormData(prev => ({
                        ...prev, ...User,
                        Roles: response.response_data.Roles?.map(r => ({ ...r })) || [],
                    }));
                    setFormDataClaim(prev => ({
                        ...prev, ...User,
                        Claim: response.response_data.Claims?.map(c => ({ ...c })) || [],
                    }));
                }
            })
            .catch(err => console.error('Error fetching User Details:', err));

        fetchData('UserApi', `/GetUserLoginBranches/${id}`, token)
            .then(response => {
                if (response.responseCode === '1111')
                    setFormDataBranch({ Branches: response.response_data || [] });
            })
            .catch(err => console.error('Error fetching user branches:', err));
    }, [id]);

    const saveRoles = async (updatedRoles) => {
        try {
            setIsSavingRoles(true);
            const updated = { ...formData, Roles: updatedRoles || formData.Roles };
            const response = await updateData('UserApi', `SaveUserRoles/tmp/${id}`, updated, token);
            if (response.responseCode === '1111') { setSuccessMessage('Roles saved successfully'); setFormData(updated); }
            else setErrorMessage('Failed to save roles');
            setIsEditMode(false);
        } catch { setErrorMessage('Error saving roles'); }
        finally { setIsSavingRoles(false); }
    };

    const saveClaims = async (updatedClaims) => {
        try {
            setIsSavingClaims(true);
            const updated = { ...formDataClaim, Claim: updatedClaims || formDataClaim.Claim };
            const response = await updateData('UserApi', `SaveUserClaims/UpdateClaim/tmp/${id}`, updated, token);
            if (response.responseCode === '1111') { setSuccessMessage('Claims saved successfully'); setFormDataClaim(updated); }
            else setErrorMessage('Failed to save claims');
            setIsClaimEditMode(false);
        } catch { setErrorMessage('Error saving claims'); }
        finally { setIsSavingClaims(false); }
    };

    const openBranchManager = async () => {
        try {
            setIsLoadingBranches(true);
            setIsBranchEditMode(true);
            const branchRes  = await fetchData('BranchApi', `/${companyId}`, token);
            const allBranches = branchRes.response_data || branchRes || [];
            const assignedMap = {};
            (formDataBranch.Branches || []).forEach(b => { assignedMap[b.BranchId] = b; });
            const merged = allBranches.map(b => {
                const existing = assignedMap[b.Id] || assignedMap[b.BranchId];
                return {
                    Id: existing?.Id || 0, CompanyId: companyId,
                    BranchId: b.Id || b.BranchId, BranchName: b.Name || b.BranchName || '',
                    UserId: Number(id), IsActive: existing ? existing.IsActive : false,
                    CreatedBy: existing?.CreatedBy || 0, CreatedDate: existing?.CreatedDate || null,
                    ModifiedBy: existing?.ModifiedBy || 0, ModifiedDate: existing?.ModifiedDate || null,
                };
            });
            setFormDataBranch({ Branches: merged });
        } catch (err) {
            console.error('Error loading branches:', err);
            setErrorMessage('Failed to load branch list');
        } finally { setIsLoadingBranches(false); }
    };

    const saveBranches = async (updatedBranches) => {
        try {
            setIsSavingBranches(true);
            const payload = updatedBranches.map(b => ({
                Id: b.Id || 0, CompanyId: b.CompanyId || companyId,
                BranchId: b.BranchId, BranchName: b.BranchName,
                UserId: Number(id), IsActive: b.IsActive,
                CreatedBy: b.CreatedBy || Number(userId), CreatedDate: b.CreatedDate || null,
                ModifiedBy: Number(userId), ModifiedDate: b.ModifiedDate || null,
            }));
            const response = await postDataNew('UserApi', 'SaveUserLoginBranches', id, payload, token);
            if (response.responseCode === '1111') { setSuccessMessage('Branch permissions saved successfully'); setFormDataBranch({ Branches: updatedBranches }); }
            else setErrorMessage('Failed to save branch permissions');
            setIsBranchEditMode(false);
        } catch { setErrorMessage('Error saving branch permissions'); }
        finally { setIsSavingBranches(false); }
    };

    // ── Shared styles ──
    const readInput = {
        width: '100%', padding: '6px 10px', fontSize: '12px',
        borderRadius: '7px', border: '1px solid #e2e8f0',
        background: '#f8f9fa', outline: 'none', fontFamily: 'Times New Roman',
        cursor: 'not-allowed', color: '#4a5568', boxSizing: 'border-box',
    };

    const labelStyle = {
        display: 'block', fontSize: '10px', fontWeight: '700',
        color: '#718096', marginBottom: '3px',
        textTransform: 'uppercase', letterSpacing: '0.4px',
    };

    const cardStyle = {
        background: 'white', borderRadius: '10px', padding: '14px 16px',
        marginBottom: '8px', border: '1px solid #e2e8f0',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    };

    const sectionHeader = (icon, title, action) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', paddingBottom: '7px', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                <div style={{ background: 'linear-gradient(135deg,#2C3E6E,#3d5491)', borderRadius: '6px', padding: '5px', display: 'flex' }}>
                    {icon}
                </div>
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#2C3E6E', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {title}
                </span>
            </div>
            {action}
        </div>
    );

    const statBox = (value, label, bg) => (
        <div style={{ background: bg, padding: '8px 10px', borderRadius: '7px', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
            <span style={{ fontSize: '20px', fontWeight: '700', lineHeight: 1 }}>{value}</span>
            <span style={{ fontSize: '10px', opacity: 0.9 }}>{label}</span>
        </div>
    );

    const manageBtn = (onClick) => (
        <Tooltip title="Manage" arrow>
            <Button
                onClick={onClick}
                variant="contained"
                size="small"
                startIcon={<EditIcon sx={{ fontSize: '12px' }} />}
                sx={{
                    background: 'linear-gradient(135deg,#2C3E6E,#3d5491)',
                    color: 'white', padding: '3px 10px', borderRadius: '6px',
                    fontSize: '10px', fontWeight: '700', minHeight: '24px',
                    textTransform: 'none', boxShadow: '0 2px 6px rgba(44,62,110,0.25)',
                    '&:hover': { background: 'linear-gradient(135deg,#1a2a4a,#2C3E6E)' }
                }}
            >
                Manage
            </Button>
        </Tooltip>
    );

    const activeBadge = (items, label) => items?.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {items.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 9px', background: 'rgba(44,62,110,0.07)', borderRadius: '5px', border: '1px solid rgba(44,62,110,0.2)' }}>
                    <CheckCircleIcon style={{ fontSize: '11px', color: '#2C3E6E' }} />
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#2C3E6E' }}>{item}</span>
                </div>
            ))}
        </div>
    ) : (
        <p style={{ fontSize: '11px', color: '#a0aec0', margin: 0, padding: '10px 0' }}>
            No active {label}. Click "Manage" to assign.
        </p>
    );

    return (
        <div style={{ minWidth: '100%', background: '#f0f2f5', padding: '10px', fontFamily: 'Times New Roman' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>

                {/* ── Gradient Header ── */}
                <div style={{
                    background: 'linear-gradient(135deg, #2C3E6E 0%, #3d5491 100%)',
                    borderRadius: '12px', padding: '10px 16px', marginBottom: '10px',
                    color: 'white', boxShadow: '0 4px 12px rgba(44,62,110,0.25)',
                    display: 'flex', alignItems: 'center', gap: '12px',
                }}>
                    {/* Avatar */}
                    <div style={{
                        width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0,
                        background: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.35)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '13px', fontWeight: '700',
                    }}>
                        {(formData.FirstName?.[0] || '').toUpperCase()}{(formData.LastName?.[0] || '').toUpperCase()}
                    </div>

                    {/* Name + meta */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: '700', letterSpacing: '0.2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {formData.FirstName || formData.LastName ? `${formData.FirstName} ${formData.LastName}` : 'User Details'}
                        </div>
                        <div style={{ fontSize: '11px', opacity: 0.82, marginTop: '1px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {formData.UserName      && <span>@{formData.UserName}</span>}
                            {formData.Email         && <span>{formData.Email}</span>}
                            {formData.ContactNumber && <span>{formData.ContactNumber}</span>}
                        </div>
                    </div>

                    {/* Category pills */}
                    <div style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
                        {[
                            { icon: '👤', label: 'Personal Info' },
                            { icon: '🔐', label: 'Claims' },
                            { icon: '🛡️', label: 'Role' },
                            { icon: '🏢', label: 'Branch Permissions' },
                        ].map(s => (
                            <span key={s.label} style={{
                                background: 'rgba(255,255,255,0.15)', padding: '3px 9px',
                                borderRadius: '5px', fontSize: '11px', fontWeight: '600',
                                display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
                            }}>
                                {s.icon} {s.label}
                            </span>
                        ))}
                    </div>

                    {/* Back button */}
                    <Button
                        variant="outlined"
                        component={Link}
                        to="/AuthConsole"
                        startIcon={<ArrowBackIcon sx={{ fontSize: '13px' }} />}
                        sx={{
                            borderColor: 'rgba(255,255,255,0.5)', color: 'white', flexShrink: 0,
                            padding: '4px 12px', borderRadius: '7px', fontSize: '11px',
                            fontWeight: '700', textTransform: 'none', minHeight: '28px',
                            '&:hover': { borderColor: 'white', background: 'rgba(255,255,255,0.1)' }
                        }}
                    >
                        Back
                    </Button>
                </div>

                {/* ── Personal Information — 5 columns ── */}
                <div style={cardStyle}>
                    {sectionHeader(<PersonIcon style={{ fontSize: '14px', color: 'white' }} />, 'Personal Information')}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
                        {[
                            { label: 'First Name',     value: formData.FirstName     },
                            { label: 'Last Name',      value: formData.LastName      },
                            { label: 'Username',       value: formData.UserName      },
                            { label: 'Contact Number', value: formData.ContactNumber },
                            { label: 'Email Address',  value: formData.Email         },
                        ].map(({ label, value }) => (
                            <div key={label}>
                                <label style={labelStyle}>{label}</label>
                                <input value={value || ''} type="text" readOnly style={readInput} />
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Claims · Role · Branches — 3 columns ── */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>

                    {/* Claims */}
                    <div style={cardStyle}>
                        {sectionHeader(
                            <SecurityIcon style={{ fontSize: '14px', color: 'white' }} />,
                            'User Claims',
                            manageBtn(() => setIsClaimEditMode(true))
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '10px' }}>
                            {statBox(formDataClaim.Claim?.length || 0, 'Total', '#2C3E6E')}
                            {statBox(formDataClaim.Claim?.filter(c => c.IsSelected).length || 0, 'Active', 'linear-gradient(135deg,#11998e,#38ef7d)')}
                        </div>
                        <div style={{ background: '#f8f9fa', borderRadius: '7px', padding: '8px', minHeight: '60px', maxHeight: '180px', overflowY: 'auto' }}>
                            <div style={{ fontSize: '10px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '6px' }}>Active Claims</div>
                            {activeBadge(
                                formDataClaim.Claim?.filter(c => c.IsSelected).map(c => c.ClaimValue),
                                'claims'
                            )}
                        </div>
                    </div>

                    {/* Role */}
                    <div style={cardStyle}>
                        {sectionHeader(
                            <AdminPanelSettingsIcon style={{ fontSize: '14px', color: 'white' }} />,
                            'User Role',
                            manageBtn(() => setIsEditMode(true))
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '10px' }}>
                            {statBox(formData.Roles?.length || 0, 'Total', '#2C3E6E')}
                            {statBox(formData.Roles?.filter(r => r.IsSelected).length || 0, 'Active', 'linear-gradient(135deg,#667eea,#764ba2)')}
                        </div>
                        <div style={{ background: '#f8f9fa', borderRadius: '7px', padding: '8px', minHeight: '60px', maxHeight: '180px', overflowY: 'auto' }}>
                            <div style={{ fontSize: '10px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '6px' }}>Active Role</div>
                            {activeBadge(
                                formData.Roles?.filter(r => r.IsSelected).map(r => r.RoleName),
                                'role'
                            )}
                        </div>
                    </div>

                    {/* Branch Permissions */}
                    <div style={cardStyle}>
                        {sectionHeader(
                            <StoreIcon style={{ fontSize: '14px', color: 'white' }} />,
                            'Branch Permissions',
                            manageBtn(openBranchManager)
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '10px' }}>
                            {statBox(formDataBranch.Branches?.length || 0, 'Total', '#2C3E6E')}
                            {statBox(formDataBranch.Branches?.filter(b => b.IsActive).length || 0, 'Allowed', 'linear-gradient(135deg,#11998e,#38ef7d)')}
                        </div>
                        <div style={{ background: '#f8f9fa', borderRadius: '7px', padding: '8px', minHeight: '60px', maxHeight: '180px', overflowY: 'auto' }}>
                            <div style={{ fontSize: '10px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '6px' }}>Allowed Branches</div>
                            {activeBadge(
                                formDataBranch.Branches?.filter(b => b.IsActive).map(b => b.BranchName),
                                'branches'
                            )}
                        </div>
                    </div>
                </div>

            </div>

            {/* ── Dialogs ── */}
            <Dialog open={isClaimEditMode} onClose={() => setIsClaimEditMode(false)} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: '14px', maxHeight: '90vh' } }}>
                <ClaimsManager claims={formDataClaim.Claim || []} onSave={saveClaims} onCancel={() => setIsClaimEditMode(false)} isSaving={isSavingClaims} />
            </Dialog>

            <Dialog open={isEditMode} onClose={() => setIsEditMode(false)} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: '14px', maxHeight: '90vh' } }}>
                <RolesManager roles={formData.Roles || []} onSave={saveRoles} onCancel={() => setIsEditMode(false)} isSaving={isSavingRoles} />
            </Dialog>

            <Dialog open={isBranchEditMode} onClose={() => setIsBranchEditMode(false)} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: '14px', maxHeight: '90vh' } }}>
                <BranchManager
                    branches={formDataBranch.Branches || []}
                    onSave={saveBranches}
                    onCancel={() => setIsBranchEditMode(false)}
                    isSaving={isSavingBranches}
                    isLoading={isLoadingBranches}
                />
            </Dialog>

            <Snackbar open={!!errorMessage || !!successMessage} autoHideDuration={4000} onClose={() => { setErrorMessage(''); setSuccessMessage(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert onClose={() => { setErrorMessage(''); setSuccessMessage(''); }} severity={errorMessage ? 'error' : 'success'} sx={{ fontSize: '12px' }}>
                    {errorMessage || successMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default UserDetail;
